import { useState } from "react";
import { X, Bug, Lightbulb, MessageSquare, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Small feedback modal used everywhere in the app. Accepts bug reports and
 * suggestions. Works signed-in (email auto-filled) or anonymous.
 */
export default function FeedbackModal({ open, onClose, currentUser }) {
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please describe the issue or suggestion.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        email: currentUser ? undefined : (email.trim() || undefined),
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Thanks — this goes straight to me.");
      setMessage("");
      setEmail("");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit — please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink-900/30 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="feedback-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-ivory-200 p-5 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">Beta feedback</div>
            <h2 className="font-heading text-2xl font-bold text-ink-900 mt-1">Found a bug or have a suggestion?</h2>
            <p className="text-ink-500 text-xs mt-1">Goes straight to me. I read every one.</p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-500 hover:text-ink-900" aria-label="Close" data-testid="feedback-close-btn">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "bug", label: "Bug", icon: Bug },
              { v: "suggestion", label: "Suggestion", icon: Lightbulb },
              { v: "other", label: "Other", icon: MessageSquare },
            ].map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setKind(v)}
                className={`p-3 rounded-xl border text-sm flex flex-col items-center gap-1.5 transition ${
                  kind === v
                    ? "border-sage-500 bg-sage-100 text-sage-700"
                    : "border-ivory-200 text-ink-600 hover:border-sage-500"
                }`}
                data-testid={`feedback-kind-${v}`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>

          <div>
            <label className="label-overline block mb-2">What happened, or what could be better?</label>
            <textarea
              rows={5}
              className="input-field"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Steps to reproduce (bug) or what you'd like to see (suggestion)…"
              autoFocus
              data-testid="feedback-message-input"
            />
          </div>

          {!currentUser && (
            <div>
              <label className="label-overline block mb-2">Email (optional)</label>
              <input
                type="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com — so I can reply"
                data-testid="feedback-email-input"
              />
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="feedback-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? "Sending..." : "Send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
